import { Component, Inject, ViewChild, forwardRef } from "@angular/core";
import { NgForm } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from "@angular/material/dialog";
import { firstValueFrom } from "rxjs";
import { REQUEST_ERROR } from "../../common/constants";
import { Customer, CustomerDefaultPropertySettings, DefaultPlugin, Partner, SubscriptionSetPaymentMethod, SubscriptionSetPaymentType } from "../../model";
import { AuthenticationService } from "../../service/authentication.service";
import { CustomPropertyService, CustomPropertyType } from "../../service/custom-property.service";
import { DefaultPropertySettingsService, DefaultPropertySettingsType } from "../../service/default-property-settings.service";
import { SubscriptionSetService } from "../../service/subscription-set.service";
import { ErrorUtility } from "../../utility/error-utility";
import { CustomerEditComponent } from "../customer/customer-edit/customer-edit.component";
import { CustomerService } from "../customer/customer.service";
import { PartnerEditComponent } from "../partner/partner-edit/partner-edit.component";
import { PartnerService } from "../partner/partner.service";
import { SubscriptionSetPaymentErrorDialogComponent } from "./subscription-set-payment-error-dialog.component";
import { SubscriptionSetStripePaymentComponent } from "./subscription-set-stripe-payment.component";

@Component({
    selector: 'subscription-set-payment-dialog',
    template: require('./subscription-set-payment-dialog.component.html'),
    styles: [require('./subscription-set-page.component.css'), require('./subscription-set-thing-form.component.css')]
})
export class SubscriptionSetPaymentDialogComponent {

    @ViewChild('form') form: NgForm;

    @ViewChild('stripePayment') stripePayment: SubscriptionSetStripePaymentComponent;

    @ViewChild('customerEdit') customerEdit: CustomerEditComponent;

    @ViewChild('partnerEdit') partnerEdit: PartnerEditComponent;

    loading: boolean;
    stripeEnabled: boolean;
    deferredEnabled: boolean;
    stripePublishableKey: string;
    payWithStripe: boolean;
    currentPaymentMethod: SubscriptionSetPaymentMethod;
    mode: PaymentDialogMode;
    billingInfo: boolean;
    isCustomer: boolean
    entity: Customer | Partner;

    private paymentBody: any;
    private paymentType: SubscriptionSetPaymentType = SubscriptionSetPaymentType.OTHER;
    private subscriptionSetHash: string;
    private configurationHash: string;
    private customerId: string;
    private partnerId: string;
    private isOrganizationUser: boolean;

    constructor(
        @Inject(forwardRef(() => MatDialog)) private dialog: MatDialog,
        @Inject(forwardRef(() => MatDialogRef)) private dialogRef: MatDialogRef<SubscriptionSetPaymentDialogComponent>,
        @Inject(forwardRef(() => SubscriptionSetService)) private subscriptionSetService: SubscriptionSetService,
        @Inject(forwardRef(() => AuthenticationService)) private authenticationService: AuthenticationService,
        @Inject(forwardRef(() => CustomerService)) private customerService: CustomerService,
        @Inject(forwardRef(() => PartnerService)) private partnerService: PartnerService,
        @Inject(forwardRef(() => DefaultPropertySettingsService)) private defaultPropertySettingsService: DefaultPropertySettingsService,
        @Inject(forwardRef(() => CustomPropertyService)) private customPropertyService: CustomPropertyService,
        @Inject(MAT_DIALOG_DATA) data
    ) {
        this.isOrganizationUser = this.authenticationService.isOrganizationUser();
        this.paymentBody = data.paymentBody;
        this.subscriptionSetHash = data.subscriptionSetHash;
        this.configurationHash = data.configurationHash;
        this.currentPaymentMethod = data.currentPaymentMethod;
        this.mode = data.mode;
        if (this.isOrganizationUser) {
            this.customerId = data.customerId;
            this.partnerId = data.partnerId;
        }
        this.deferredEnabled = this.authenticationService.getTenant().deferredPaymentEnabled;
        if ((!data.paymentPresent && (this.mode != PaymentDialogMode.SET_AS_DEFAULT)) || this.isOrganizationUser) {
            this.loading = true;
            this.completeOperation();
        } else {
            if (this.mode == PaymentDialogMode.FIRST_PAYMENT) {
                this.isCustomer = this.authenticationService.isCustomerUser();
                this.entity = this.isCustomer ? this.authenticationService.getUser().customer : this.authenticationService.getUser().partner;
                this.billingInfo = this.hasBillingInfo();
            }
            this.stripeEnabled = this.authenticationService.getEnabledPlugins().find(p => p.name == DefaultPlugin.STRIPE)?.enabled;
            if (this.stripeEnabled) {
                this.subscriptionSetService.getPublishableKey().then(key => this.stripePublishableKey = key);
            }
        }
    }

    private hasBillingInfo(): boolean {
        let defaultProps = this.defaultPropertySettingsService.getTenantDefaultPropertySettingsByType(this.isCustomer ? DefaultPropertySettingsType.CUSTOMER : DefaultPropertySettingsType.PARTNER);
        let props = this.customPropertyService.getCustomPropertyDefinitionByType(this.isCustomer ? CustomPropertyType.Customer : CustomPropertyType.Partner);
        return defaultProps?.some((d: CustomerDefaultPropertySettings) => d.billingInformation) || props?.some(p => p.billingInformation);
    }

    confirmPayment(): void {
        let formValue = this.form.form.getRawValue();
        this.paymentType = formValue.paymentType;
        if (this.paymentType == SubscriptionSetPaymentType.OTHER || this.currentPaymentMethod?.valid) {
            this.loading = true;
            if (this.mode == PaymentDialogMode.SET_AS_DEFAULT) {
                this.subscriptionSetService.setDefaultPaymentMethod(SubscriptionSetPaymentType.OTHER).then(() => this.dialogRef.close(true))
            } else {
                this.completeOperation();
            }
        } else {
            this.payWithStripe = true;
        }
    }

    private completeOperation(stripeCustomerId?: string): void {
        this.subscriptionSetService.createPayment(this.paymentBody, this.subscriptionSetHash, this.configurationHash, this.paymentType, stripeCustomerId, this.customerId, this.partnerId).then(() => {
            this.dialogRef.close({ outcome: PaymentStatus.SUCCESS });
        }).catch(err => {
            if (err.status == 409) {
                const dialogConfig = new MatDialogConfig();
                dialogConfig.data = { message: ErrorUtility.getMessage(err, REQUEST_ERROR) };
                this.dialog.open(SubscriptionSetPaymentErrorDialogComponent, dialogConfig);
                this.dialogRef.close();
            } else {
                this.dialogRef.close({ outcome: PaymentStatus.FAILED, message: err.error.message });
            }
        });
    }

    pay(): void {
        this.loading = true;
        this.stripePayment.submit().then(resp => {
            if (resp.error) {
                this.loading = false;
                return;
            }
            this.stripePayment.confirmSetup().then(resp => {
                if (resp.error) {
                    this.loading = false;
                    return;
                }
                this.subscriptionSetService.setDefaultPaymentMethod(SubscriptionSetPaymentType.STRIPE, resp.setupIntent.payment_method, this.stripePayment.stripeCustomerId).then(() => {
                    if (this.mode == PaymentDialogMode.SET_AS_DEFAULT) {
                        this.dialogRef.close({ outcome: PaymentStatus.SUCCESS });
                    } else if (this.mode == PaymentDialogMode.RETRY_PAYMENT) {
                        this.subscriptionSetService.pay(this.paymentBody.id).then(() => this.dialogRef.close({ outcome: PaymentStatus.SUCCESS })).catch(err => {
                            this.dialogRef.close({ outcome: PaymentStatus.FAILED, message: err.error.message });
                        });
                    } else { // FIRST_PAYMENT
                        this.completeOperation(this.stripePayment.stripeCustomerId);
                    }
                });
            });
        })
    }

    confirmBillingInfo(): void {
        const body = this.getBody();
        this.getSaveEntityRequest(body).then(() => {
            this.billingInfo = false;
            this.authenticationService.refreshCurrentUser();
        })
    }


    private getSaveEntityRequest(body: any): Promise<any> {
        if (this.isCustomer) {
            return firstValueFrom(this.customerService.saveCustomer(body, this.entity as Customer));
        } else {
            return firstValueFrom(this.partnerService.savePartner(body, this.entity as Partner));
        }
    }

    private getBody(): any {
        const formValue = this.isCustomer ? this.customerEdit.getBody() : this.partnerEdit.getBody();
        let body = Object.assign({}, this.entity);
        for (let [key, value] of Object.entries(formValue)) {
            if (key == 'properties') {
                if (value) {
                    const properties = {};
                    for (let [propertyName, propertyValue] of Object.entries(value)) {
                        properties[propertyName] = propertyValue;
                    }
                    body['properties'] = properties;
                }
            } else {
                body[key] = value;
            }
        }
        return body;
    }
}

export enum PaymentStatus {
    SUCCESS = 'SUCCESS',
    FAILED = 'FAILED'
}

export enum PaymentDialogMode {
    FIRST_PAYMENT = 'FIRST_PAYMENT',
    SET_AS_DEFAULT = 'SET_AS_DEFAULT',
    RETRY_PAYMENT = 'RETRY_PAYMENT'
}